import React from 'react';
import {
    Box,
    Button,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Input, Select, MultiSelect } from '../../Common';
import { Grey2, Grey4 } from '../../../color_utils';
import {
    FIELDS,
    OPERATORS_BY_KIND,
    TAG_RECENCY_OPTIONS,
    conditionNeedsValue,
    getField,
    emptyCondition,
    emptyGroup,
    defaultValueFor,
    generateExpression,
} from './expressionCatalog';

// No-code editor for a rule expression. The user composes conditions from the
// curated field catalog with AND/OR groups; the RulesEngine expression is
// generated from this state and previewed live at the bottom. State is a
// plain AST ({ combinator, items }) owned by the parent, which also persists
// it on the rule as BuilderState so builder-authored rules round-trip.

const fieldOptions = FIELDS.map(f => ({ value: f.key, name: f.label }));

const ConditionRow = ({ condition, onDelete, refresh, tagOptions }) => {
    const field = getField(condition.field);
    const operators = field ? OPERATORS_BY_KIND[field.kind] : [];
    const showValue = conditionNeedsValue(field, condition.operator);
    const tagRecency = field && field.kind === 'tags'
        ? (condition.value.recency ?? (String(condition.value.days || '').trim() !== '' ? 'within' : ''))
        : '';

    return (
        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <Box sx={{ width: 250, flexShrink: 0 }}>
                <Select
                    label="Field"
                    variant="outlined"
                    options={fieldOptions}
                    value={condition.field}
                    onChange={e => {
                        const nextField = getField(e.target.value);
                        condition.field = e.target.value;
                        condition.operator = nextField ? OPERATORS_BY_KIND[nextField.kind][0].value : '';
                        condition.value = nextField ? defaultValueFor(nextField) : '';
                        refresh();
                    }}
                />
            </Box>
            {field && (
                <Box sx={{ width: 170, flexShrink: 0 }}>
                    <Select
                        label="Operator"
                        variant="outlined"
                        showNullOption={false}
                        options={operators}
                        value={condition.operator}
                        onChange={e => {
                            condition.operator = e.target.value;
                            refresh();
                        }}
                    />
                </Box>
            )}
            {showValue && field.kind === 'tags' && (
                <Box sx={{ flexGrow: 1, display: 'flex', gap: '10px', minWidth: 0 }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <MultiSelect
                            variant="outlined"
                            label="Tags"
                            options={tagOptions.map(name => ({ value: name, name }))}
                            value={condition.value.names}
                            onChange={e => {
                                const { target: { value } } = e;
                                condition.value.names = typeof value === 'string' ? value.split(',') : value;
                                refresh();
                            }}
                        />
                    </Box>
                    <Box sx={{ width: 160, flexShrink: 0 }}>
                        <Select
                            label="Recency"
                            variant="outlined"
                            showNullOption={false}
                            options={TAG_RECENCY_OPTIONS.map(o => ({ value: o.value || 'any', name: o.name }))}
                            value={tagRecency || 'any'}
                            onChange={e => {
                                condition.value.recency = e.target.value === 'any' ? '' : e.target.value;
                                refresh();
                            }}
                        />
                    </Box>
                    {(tagRecency === 'within' || tagRecency === 'atleast') && (
                        <Box sx={{ width: 100, flexShrink: 0 }}>
                            <Input
                                fullWidth
                                variant="outlined"
                                label="Days"
                                type="number"
                                value={condition.value.days}
                                onChange={e => {
                                    condition.value.days = e.target.value;
                                    refresh();
                                }}
                            />
                        </Box>
                    )}
                </Box>
            )}
            {showValue && field.kind !== 'tags' && (
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Input
                        fullWidth
                        variant="outlined"
                        label="Value"
                        type={field.kind === 'number' ? 'number' : 'text'}
                        helperText={field.hint || ''}
                        alwaysShowHelper={!!field.hint}
                        value={condition.value}
                        onChange={e => {
                            condition.value = e.target.value;
                            refresh();
                        }}
                    />
                </Box>
            )}
            <Tooltip title="Remove condition">
                <IconButton size="small" onClick={onDelete} sx={{ mt: '10px' }}>
                    <DeleteOutlineIcon sx={{ color: Grey2, fontSize: 20 }} />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

const AND_COLOR = '#1668B8';
const OR_COLOR = '#C77800';

const CombinatorChip = ({ combinator }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', ml: '4px', my: '-4px' }}>
        <Typography
            sx={{
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '1px',
                color: '#fff',
                backgroundColor: combinator === 'or' ? OR_COLOR : AND_COLOR,
                borderRadius: '10px',
                padding: '1px 10px',
            }}
        >
            {combinator === 'or' ? 'OR' : 'AND'}
        </Typography>
        <Box sx={{ flexGrow: 1, borderTop: `1px dashed ${Grey4}` }} />
    </Box>
);

const GroupEditor = ({ group, refresh, onDelete, tagOptions, depth }) => (
    <Box
        sx={{
            border: `1px ${depth > 0 ? 'dashed' : 'solid'} ${Grey4}`,
            borderLeft: `3px solid ${group.combinator === 'or' ? OR_COLOR : AND_COLOR}`,
            borderRadius: '8px',
            p: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            backgroundColor: depth % 2 === 1 ? '#fafafa' : '#fff',
        }}
    >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <ToggleButtonGroup
                exclusive
                size="small"
                value={group.combinator}
                onChange={(e, combinator) => {
                    if (combinator) {
                        group.combinator = combinator;
                        refresh();
                    }
                }}
            >
                <ToggleButton
                    value="and"
                    sx={{
                        fontSize: '11px', py: '3px', fontWeight: 700,
                        '&.Mui-selected': { backgroundColor: AND_COLOR, color: '#fff', '&:hover': { backgroundColor: AND_COLOR } },
                    }}
                >
                    ALL (AND)
                </ToggleButton>
                <ToggleButton
                    value="or"
                    sx={{
                        fontSize: '11px', py: '3px', fontWeight: 700,
                        '&.Mui-selected': { backgroundColor: OR_COLOR, color: '#fff', '&:hover': { backgroundColor: OR_COLOR } },
                    }}
                >
                    ANY (OR)
                </ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Button
                    size="small"
                    startIcon={<AddIcon />}
                    sx={{ fontSize: '11px' }}
                    onClick={() => {
                        group.items.push(emptyCondition());
                        refresh();
                    }}
                >
                    Condition
                </Button>
                <Button
                    size="small"
                    startIcon={<AddIcon />}
                    sx={{ fontSize: '11px' }}
                    onClick={() => {
                        group.items.push({ type: 'group', ...emptyGroup() });
                        refresh();
                    }}
                >
                    Group
                </Button>
                {onDelete && (
                    <Tooltip title="Remove group">
                        <IconButton size="small" onClick={onDelete}>
                            <DeleteOutlineIcon sx={{ color: Grey2, fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
        {group.items.length === 0 && (
            <Typography sx={{ color: Grey2, fontSize: '12px' }}>
                Add a condition to get started.
            </Typography>
        )}
        {group.items.map((item, index) => (
            <React.Fragment key={index}>
                {index > 0 && <CombinatorChip combinator={group.combinator} />}
                {item.type === 'group' ? (
                    <GroupEditor
                        group={item}
                        refresh={refresh}
                        tagOptions={tagOptions}
                        depth={depth + 1}
                        onDelete={() => {
                            group.items.splice(index, 1);
                            refresh();
                        }}
                    />
                ) : (
                    <ConditionRow
                        condition={item}
                        refresh={refresh}
                        tagOptions={tagOptions}
                        onDelete={() => {
                            group.items.splice(index, 1);
                            refresh();
                        }}
                    />
                )}
            </React.Fragment>
        ))}
    </Box>
);

const RuleExpressionBuilder = ({ value, onChange, tagOptions }) => {
    const refresh = () => onChange({ ...value });
    const { expression, errors } = generateExpression(value);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <GroupEditor group={value} refresh={refresh} tagOptions={tagOptions || []} depth={0} />
            <Box
                sx={{
                    border: `1px solid ${Grey4}`,
                    borderRadius: '6px',
                    p: '10px',
                    backgroundColor: '#fafafa',
                }}
            >
                <Typography sx={{ color: Grey2, fontSize: '10px', textTransform: 'uppercase', mb: 0.5 }}>
                    Generated expression
                </Typography>
                {expression ? (
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {expression}
                    </Typography>
                ) : (
                    <Typography sx={{ color: Grey2, fontSize: '12px', fontStyle: 'italic' }}>
                        {errors[0]}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default RuleExpressionBuilder;
